import { ensureUrlAuthBootstrap } from "../urlAuthBootstrap";
import { hasActiveKeycloakSession, keycloak } from "../keycloak";
import {
  clearAllBrowserStorage,
  getToken,
  setToken,
} from "../../services/storageService";
import {
  hasAppLoggedOutFlag,
  logoutOnSessionExpired,
  onKeycloakAuthLogout,
} from "../logout";
import { readCachedSessionUser } from "./authSession.storage";
import {
  callbackUrlHasToken,
  getAuthCallbackPath,
  isAuthCallbackPath,
} from "./authSession.service";
import { clearLoginSelection } from "../../utils/matchesAssignedApplicationOrigin";
import { buildGatewayLoginUrlWithAppContext, readThisAppLoginContext } from "./gatewayLoginContext";
import {
  isSsoPrefetchRequest,
  notifySsoPrefetchComplete,
} from "./ssoPrefetch";
import { resolveGatewayLoginApiBase } from "../../runtime/gatewayEnv";

export { getAuthCallbackPath };

/** Gateway may land token+endpoint on `/` — bounce to callback path before bootstrap strips params. */
function redirectTokenCallbackToAuthCallbackPath(): boolean {
  if (typeof window === "undefined") return false;
  if (isAuthCallbackPath()) return false;
  const params = new URLSearchParams(window.location.search);
  const hasToken = params.has("token") || params.has("auth");
  if (!hasToken || !params.has("endpoint")) return false;
  const callbackPath = getAuthCallbackPath();
  window.location.replace(
    callbackPath + window.location.search + window.location.hash,
  );
  return true;
}

async function setPostLoginTargetForThisApp(): Promise<void> {
  if (typeof window === "undefined") return;

  const base = resolveGatewayLoginApiBase();
  const { url: appUrl, name: appName, keycloakClientId, applicationId } =
    readThisAppLoginContext();

  try {
    await fetch(`${base}/login/select-app`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      signal: AbortSignal.timeout(1500),
      body: JSON.stringify({
        url: appUrl,
        name: appName,
        keycloakClientId,
        applicationId,
      }),
    });
  } catch {
    // Best-effort only — fallback is existing cookie selection.
  }
}

function registerKeycloakHandlers(): void {
  keycloak.onTokenExpired = () => {
    if (!hasActiveKeycloakSession()) return;
    keycloak
      .updateToken(30)
      .then(() => {
        if (keycloak.token) setToken(keycloak.token);
      })
      .catch(() => {
        void logoutOnSessionExpired();
      });
  };

  keycloak.onAuthRefreshSuccess = () => {
    if (keycloak.token) setToken(keycloak.token);
  };

  keycloak.onAuthLogout = () => {
    onKeycloakAuthLogout();
  };
}

export type BootstrapAppAuthOptions = {
  renderApp: () => void;
};

/**
 * Single entry point every client app calls from index.tsx.
 * Handles login redirect URL, token bootstrap, and gateway login redirect.
 */
export async function bootstrapAppAuth(options: BootstrapAppAuthOptions): Promise<void> {
  // Prefetch iframes must never call select-app or bounce to login — that
  // creates a tight me/select-app retry loop when the gateway returns 4xx/5xx.
  if (isSsoPrefetchRequest()) {
    if (!hasAppLoggedOutFlag()) {
      await ensureUrlAuthBootstrap();
      const token = getToken();
      const cached = readCachedSessionUser();
      if (token && cached && hasActiveKeycloakSession()) {
        registerKeycloakHandlers();
      }
    }
    notifySsoPrefetchComplete();
    return;
  }

  if (redirectTokenCallbackToAuthCallbackPath()) {
    return;
  }

  if (isAuthCallbackPath() && callbackUrlHasToken()) {
    options.renderApp();
    return;
  }

  await ensureUrlAuthBootstrap();

  const existingToken = getToken();
  const cachedSession = readCachedSessionUser();

  if (!hasAppLoggedOutFlag() && existingToken && cachedSession) {
    if (hasActiveKeycloakSession()) {
      registerKeycloakHandlers();
    }
    options.renderApp();
    return;
  }

  if (existingToken && !cachedSession) {
    clearAllBrowserStorage();
  }

  if (!isAuthCallbackPath()) {
    await setPostLoginTargetForThisApp();
    clearLoginSelection();
    window.location.replace(
      buildGatewayLoginUrlWithAppContext({
        forceLogin: hasAppLoggedOutFlag(),
      }),
    );
    return;
  }

  options.renderApp();
}
